import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, Trash2, Lock } from "lucide-react";

// Suppression définitive d'une entreprise (Super Admin) : pré-check, blocages, saisie exacte du nom.
export function DeleteOrgDialog({ org, onClose, onDeleted }) {
  const [prev, setPrev] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/super/organizations/${org.id}/deletion-preview`).then((r) => setPrev(r.data)).catch((e) => setError(apiError(e.response?.data?.detail) || e.message));
  }, [org.id]);

  const blocked = !prev || prev.blockers.length > 0;
  const needAck = prev?.real_invoices > 0;
  const canDelete = !blocked && name.trim() === org.name.trim() && (!needAck || ack) && !busy;
  const rows = prev ? Object.values(prev.counts).filter((c) => c.count > 0) : [];

  const run = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.delete(`/super/organizations/${org.id}`, { data: { confirm_name: name, acknowledge_invoices: ack } });
      toast.success(`« ${data.organization_name} » supprimée définitivement`);
      onDeleted();
    } catch (e) { setError(apiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="delete-org-dialog" className="max-w-md w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto rounded-2xl" onInteractOutside={(e) => busy && e.preventDefault()} onEscapeKeyDown={(e) => busy && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700 flex items-center gap-2"><Trash2 className="w-5 h-5" />Supprimer définitivement</DialogTitle>
          <DialogDescription>Opération irréversible. Réservée aux entreprises de test ou aux données devant réellement être effacées. Pour une entreprise réelle, privilégiez l'archivage.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="font-bold text-red-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{org.name} <span className="font-mono text-xs text-red-600">({org.code})</span></p>
            {!prev && !error && <p className="text-red-700 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Analyse des données liées…</p>}
            {prev && (
              <ul data-testid="delete-preview" className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-red-900">
                {rows.map((c) => <li key={c.label} className="flex justify-between gap-2"><span className="truncate">{c.label}</span><b>{c.count}</b></li>)}
                {rows.length === 0 && <li className="col-span-2 text-red-700">Aucune donnée liée hormis la fiche entreprise.</li>}
              </ul>
            )}
            {prev && <p className="text-[11px] text-red-700 mt-2">Total : {prev.total} enregistrement(s). La base commune des établissements SanitDocs n'est jamais supprimée. {prev.stripe_customer_id ? "L'historique conservé côté Stripe n'est pas effacé." : ""}</p>}
          </div>
          {prev?.blockers.map((b, i) => <p key={i} data-testid={`delete-blocker-${i}`} className="rounded-lg bg-orange-50 border border-orange-200 text-orange-800 p-2.5 text-xs font-semibold flex gap-1.5"><Lock className="w-4 h-4 shrink-0" />{b}</p>)}
          {needAck && !blocked && (
            <label className="flex items-start gap-2 text-xs text-slate-700 rounded-lg border border-orange-200 bg-orange-50 p-2.5">
              <input type="checkbox" data-testid="delete-ack-invoices" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 w-4 h-4" />
              <span><b>{prev.real_invoices} facture(s) réelle(s)/payée(s)</b> seront supprimées. Je confirme qu'aucune obligation de conservation ne s'applique (entreprise de test).</span>
            </label>
          )}
          {!blocked && (
            <div>
              <p className="text-slate-700 mb-1">Pour confirmer, saisissez exactement le nom de l'entreprise : <b className="select-all">{org.name}</b></p>
              <Input data-testid="delete-confirm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={org.name} className="h-11" autoComplete="off" disabled={busy} />
            </div>
          )}
          {error && <p data-testid="delete-error" className="text-red-600 text-sm">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="delete-cancel" variant="outline" onClick={onClose} disabled={busy} className="h-11">Annuler</Button>
          <Button data-testid="delete-confirm" onClick={run} disabled={!canDelete} className="h-11 bg-red-600 hover:bg-red-700 text-white">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Supprimer définitivement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
